import { supabase } from "../supabase/SupabaseClient";

/* Mensajes internos y libreta de contactos.
 *
 * Mandar va por función y no por insert directo: así el remitente lo pone el
 * servidor. Con un insert, cualquiera podría mandar un mensaje firmado por otro
 * — y un chat donde no se puede confiar en quién escribió es peor que no tener
 * chat.
 *
 * Leer va directo a la tabla, que la política de acceso ya filtra a lo mío.
 */

export async function traerLibreta() {
  const { data, error } = await supabase.rpc("libreta");
  if (error) throw error;
  return data || [];
}

export async function traerConversaciones() {
  const { data, error } = await supabase.rpc("mis_conversaciones");
  if (error) throw error;
  return data || [];
}

export async function traerHilo(con) {
  const { data, error } = await supabase
    .from("mensajes")
    .select("id, de, para, cuerpo, creado_en, recibido_en, leido_en, pendiente_en, ticket")
    .or(`de.eq.${con},para.eq.${con}`)
    .order("creado_en")
    .limit(300);
  if (error) throw error;
  return data || [];
}

export async function mandarMensaje(para, cuerpo) {
  const { data, error } = await supabase.rpc("mandar_mensaje", {
    p_para: para,
    p_cuerpo: cuerpo,
  });
  if (error) throw error;
  return data;
}

/* "Recibido" acá significa LLEGÓ A SU PANTALLA, no "está en el servidor". Lo
 * marca el navegador del que recibe.
 *
 * En una app web sin notificaciones push eso tiene una consecuencia que vale la
 * pena que se note: quien no tiene FiBOT abierto no recibe nada, y el mensaje
 * se queda en un tilde. Eso informa — quiere decir "todavía no lo tiene", que
 * es justo lo que hay que saber antes de decidir si uno levanta el teléfono. */
export async function marcarRecibidos() {
  const { error } = await supabase.rpc("marcar_recibidos");
  if (error) throw error;
}

/* Lo marca QUIEN RECIBE, para su propia memoria.
 *
 * Existe porque el mensaje sobrevive pero la obligación no: "hacé esto para
 * mañana" se lee, el contador baja a cero, la conversación se hunde y todo
 * parece atendido. Anotarlo lo mantiene vivo hasta que uno lo saque. */
export async function marcarPendiente(id, si) {
  const { error } = await supabase.rpc("marcar_pendiente", {
    p_id: id,
    p_si: si,
  });
  if (error) throw error;
}

/* Mueve el trabajo a donde vive el trabajo. No mezcla las dos cosas: un ticket
 * tiene dueño y estado, un mensaje es una pregunta sin ceremonia. Que sean dos
 * lugares es a propósito — lo que faltaba era el puente. */
export async function mensajeATicket(id, area, titulo) {
  const { data, error } = await supabase.rpc("mensaje_a_ticket", {
    p_id: id,
    p_area: area,
    p_titulo: titulo || null,
  });
  if (error) throw error;
  return data;
}

export async function marcarLeidos(con) {
  const { error } = await supabase.rpc("marcar_leidos", { p_con: con });
  if (error) throw error;
}

export async function guardarMiContacto({ mail, interno, celular, nota }) {
  const { error } = await supabase.from("contacto").upsert(
    {
      mail: mail.toLowerCase(),
      interno: interno || null,
      celular: celular || null,
      nota: nota || null,
      actualizado_en: new Date().toISOString(),
    },
    { onConflict: "mail" }
  );
  if (error) throw error;
}

/* Avisa cuando entra un mensaje nuevo.
 *
 * Sin esto el mensaje aparece recién cuando la persona recarga la página, que
 * es exactamente el problema que esto vino a resolver: alguien pregunta, el
 * otro se desocupa a los dos minutos y no se entera hasta que vuelve a mirar.
 *
 * Devuelve la función para desuscribirse. Olvidarse de llamarla deja la
 * conexión abierta cada vez que se entra a la pantalla.
 *
 * El canal lleva un número, como `alCambiarPlanilla` (services/espacio.js).
 * La pantalla escucha DOS veces a la vez —la lista de conversaciones y la
 * conversación abierta— y Supabase, si se le pide un canal con un nombre que
 * ya existe, devuelve el mismo, ya suscripto: el segundo `.on()` revienta con
 * «cannot add `postgres_changes` callbacks … after `subscribe()`» y la
 * pantalla entera se cae (Igna, 07/10/2026, al abrir Mensajes).
 */
let canalesMensajes = 0;
export function alLlegarMensaje(fn) {
  const canal = supabase
    .channel(`mensajes-nuevos:${(canalesMensajes += 1)}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "mensajes" },
      (evento) => fn(evento.new)
    )
    .subscribe();
  return () => supabase.removeChannel(canal);
}
